/**
 * World events → sound effects: the one table that says what every `WorldEvent` sounds like.
 *
 * Each rule lists every id it may play (`ids`; `prefix.*` for a family such as each skill's own
 * sound) and plays them through an `EventSfxCtx`, which places them in the world (pan, distance)
 * and can hold one back for later (a dance's beats). The game's ctx (sfx/game.ts) is made from the
 * Effects that turns the same events into visuals (render/effects.ts); while it does, the old
 * effect calls there stay silent (`sfxEngine.claimed`), so nothing sounds twice.
 *
 * An event with no rule makes no sound here (the old calls still play for it).
 */
import {
  artOfSkill,
  duelMoveOf,
  hitWeightOf,
  LIFE_ACTS,
  lifeActOfEmote,
  rankFor,
  SKILLS,
  STAGES,
  type Entity,
  type SkillDef,
  type World,
  type WorldEvent,
} from '@nine/shared';
import { isDummyMonster } from '@nine/shared';
import { diveLanding } from './duelWatch';
import { familyIdOf, planOf, type MovePlan } from './moves/plan';
import { hasSfx, type SfxOpts } from './registry';
import type { SwingWeapon } from './sounds/combat';

/** What a rule plays through. */
export interface EventSfxCtx {
  readonly world: World;
  /**
   * Plays a sound at an entity or a point in tiles (none, or the player: the listener's own, centred).
   * `o.delay` is in world seconds.
   */
  play(id: string, at?: Entity | { x: number; y: number } | null, o?: SfxOpts): void;
  /** The weapon family an entity swings with (`claw`: beasts). */
  weapon(e: Entity): SwingWeapon | 'claw';
}

type EventOf<T extends WorldEvent['type']> = Extract<WorldEvent, { type: T }>;

export interface EventRule<T extends WorldEvent['type']> {
  /** Every id this rule may play (`prefix.*`: any registered id under the prefix). */
  readonly ids: readonly string[];
  play(ev: EventOf<T>, c: EventSfxCtx): void;
}

export type EventRules = { readonly [T in WorldEvent['type']]?: EventRule<T> };

/** The skill effects that are ice (เหมันต์สะกดธาร and its school). */
const ICE = new Set(['snow', 'snowdash', 'blizzard', 'snowstep', 'icebreak', 'snowpride', 'snowblink']);

/** A move's own sound (every move has one: sfx/sounds/moves.ts), else its effect's, else its element's. */
export function skillSound(s: SkillDef): string {
  const move = `skill.move.${s.id}`;
  if (hasSfx(move)) return move;
  const own = `skill.fx.${s.fx}`;
  if (hasSfx(own)) return own;
  return `skill.el.${artOfSkill(s.id)?.element ?? 'hard'}`;
}

const PLANS = new Map<string, MovePlan>();
/** A move's plan (sfx/moves/plan.ts), kept. */
function planFor(s: SkillDef): MovePlan {
  let p = PLANS.get(s.id);
  if (!p) PLANS.set(s.id, (p = planOf(s)));
  return p;
}
/** The register a move's family layers are played in (its own sound carries the same, sounds/moves.ts). */
const LIFT = [1, 0.707, 1.414] as const;
const registerOf = (s: SkillDef): number => {
  const p = planFor(s);
  return LIFT[p.lift]! * p.size * 2 ** (p.deg / 24);
};

/** Beats heard so far of each fighter's warp chain (the blinks after its `skill` event). */
const chainBeats = new Map<number, number>();

/** A beat's strength: the first three light, then rising, the last the finisher (sfx/moves/compose.ts `composeBeat`). */
export function beatIntensity(n: number, of: number): number {
  if (n >= of) return 1.3;
  return n <= 3 ? 0.85 : 1.1;
}

const SWINGS = ['sword', 'sabre', 'fist', 'staff'].flatMap((w) => [
  `combat.swing.${w}.light`,
  `combat.swing.${w}.heavy`,
]);

const swingOf = (w: SwingWeapon | 'claw', heavy: boolean): string =>
  w === 'claw' ? 'combat.swing.claw' : `combat.swing.${w}.${heavy ? 'heavy' : 'light'}`;

const isBoss = (e: Entity | undefined): boolean => !!e?.brain?.defId.endsWith('boss');

/**
 * Whether a notice tells the player an action was refused ("ลมปราณไม่พอ", "กระเป๋าเต็ม",
 * "ยังอยู่ในการต่อสู้ ขี่พาหนะไม่ได้", "ต้องเรียน…ก่อน"): those sound as an error, the rest
 * (bought, levelled up, a warning about the world) as a plain notice. The world sends both as
 * `notice`, so this reads the words the shared rules use.
 */
export function isRefusal(text: string): boolean {
  if (/^(?:หยุดฝึก|ได้รับ|ซื้อ|ขาย|แลก|GM:)/.test(text)) return false;
  return /ไม่พอ|ไม่ได้|เต็ม|ก่อน|ยังอยู่|ยังไม่|อยู่แล้ว|ครบแล้ว|^ไม่มี|เท่านั้น|เฉพาะ|^รออีก|^ต้อง|^ตอนนี้|หมดแล้ว รอ|สูงสุดแล้ว|ถึง(?:ขั้น)? ?\+?\d+ แล้ว|^กำลัง.*อยู่$|หมด$|อีกหน่อย/.test(
    text,
  );
}

/**
 * The rank the player was last heard at (sfx/frame.ts keeps it current), to tell a training that
 * rose a stage or a whole realm from an ordinary one.
 */
export const rankSeen = { index: -1 };

export const EVENT_SFX: EventRules = {
  // ───────── combat ─────────
  hit: {
    ids: [
      'combat.dodge',
      'combat.block',
      'combat.parry',
      'combat.guardBreak',
      'combat.interrupt',
      'combat.hit.slash',
      'combat.hit.blunt',
      'combat.hit.crit',
      'combat.hurt',
      'skill.ice.shatter',
      'combat.clash.light',
      'combat.clash.strong',
      'combat.clash.heavy',
      'combat.clash.dull',
      'skill.impact.*',
      'world.dummy.wood',
      'world.dummy.straw',
    ],
    play(ev, c) {
      const w = c.world;
      const tgt = w.get(ev.tgtId);
      if (!tgt) return;
      const src = w.get(ev.srcId);
      const weapon = src ? c.weapon(src) : 'fist';
      const move = SKILLS[ev.skillId];
      const landed = ev.outcome !== 'dodge' && ev.outcome !== 'blocked' && ev.outcome !== 'interrupt';
      // The art's mark on a landed blow, in the move's own register.
      if (move && landed) c.play(`skill.impact.${familyIdOf(move)}`, tgt, { intensity: 0.7, pitch: registerOf(move) });
      // A training dummy answers in wood (plain, guarding) or dull straw-stuffed iron.
      const def = tgt.brain?.defId;
      if (isDummyMonster(def) && ev.outcome !== 'dodge')
        c.play(def === 'dummy-iron' ? 'world.dummy.straw' : 'world.dummy.wood', tgt, {
          intensity: ev.crit ? 1.2 : 0.9,
        });
      switch (ev.outcome) {
        case 'dodge':
          c.play('combat.dodge', tgt);
          break;
        case 'blocked': {
          // Steel on steel rings brighter than a blow stopped on the qi guard.
          const steel =
            weapon !== 'fist' && weapon !== 'claw' && c.weapon(tgt) !== 'fist' && c.weapon(tgt) !== 'claw';
          c.play(
            steel
              ? hitWeightOf(ev.skillId, 'hit')?.cls === 'light'
                ? 'combat.clash.light'
                : 'combat.clash.strong'
              : 'combat.clash.dull',
            tgt,
          );
          break;
        }
        case 'countered':
          // (the counter's own cue comes with the `pvpDuel` counter event)
          break;
        case 'guardShatter':
        case 'guardBreak': {
          c.play('combat.guardBreak', tgt);
          const armed = (e: Entity | undefined) => !!e && c.weapon(e) !== 'fist' && c.weapon(e) !== 'claw';
          if (armed(src) && armed(tgt)) c.play('combat.clash.heavy', tgt);
          break;
        }
        case 'interrupt':
          c.play('combat.interrupt', tgt);
          break;
        default: {
          const toPlayer = tgt === w.player;
          const big = Math.min(1.3, 0.75 + (ev.damage / Math.max(1, tgt.maxHp)) * 2);
          if (ev.crit) c.play('combat.hit.crit', tgt);
          else
            c.play(weapon === 'sword' || weapon === 'sabre' ? 'combat.hit.slash' : 'combat.hit.blunt', tgt, {
              intensity: toPlayer ? big * 0.6 : big,
            });
          const fx = SKILLS[ev.skillId]?.fx;
          if (fx && ICE.has(fx)) c.play('skill.ice.shatter', tgt, { intensity: fx === 'icebreak' ? 1 : 0.4 });
        }
      }
      if (tgt === w.player && ev.damage > 0 && ev.outcome !== 'blocked') c.play('combat.hurt');
    },
  },
  basic: {
    ids: [...SWINGS, 'combat.swing.claw'],
    play(ev, c) {
      const src = c.world.get(ev.srcId);
      if (src) c.play(swingOf(c.weapon(src), ev.step === 2), src);
    },
  },
  skill: {
    ids: [
      'skill.move.*',
      'skill.fx.*',
      'skill.el.*',
      'combat.ultimate.release',
      'skill.heal',
      'skill.buff',
      ...SWINGS,
      'combat.swing.claw',
    ],
    play(ev, c) {
      const s = SKILLS[ev.skillId];
      const src = c.world.get(ev.srcId);
      if (!s || !src) return;
      const id = skillSound(s);
      c.play(id, src);
      if (s.kind === 'power') c.play('combat.ultimate.release', src);
      if (s.blink) chainBeats.set(src.id, 0);
      // A move of its own carries its art, weapon and element already.
      if (id.startsWith('skill.move.')) return;
      const own = id.startsWith('skill.fx.');
      if (!own && (s.kind === 'attack' || s.kind === 'break')) c.play(swingOf(c.weapon(src), true), src);
      if (!own && s.kind === 'heal') c.play('skill.heal', src);
      if (!own && s.kind === 'buff') c.play('skill.buff', src);
      // The player's own moves carry their element under the move's sound.
      const el = artOfSkill(s.id)?.element;
      if (own && el && src === c.world.player) c.play(`skill.el.${el}`, src, { intensity: 0.4 });
    },
  },
  castStart: {
    ids: ['combat.breakWindup', 'combat.ultimate.charge', 'combat.charge', 'skill.windup.*'],
    play(ev, c) {
      const s = SKILLS[ev.skillId];
      const src = c.world.get(ev.srcId);
      if (!s || !src) return;
      // A break's wind-up is the cue the triangle turns on: it always gets its own warning.
      const id =
        s.kind === 'break'
          ? 'combat.breakWindup'
          : s.kind === 'power'
            ? 'combat.ultimate.charge'
            : 'combat.charge';
      c.play(id, src, { len: s.windup });
      // The art's colour under the warning (quieter: the kind's cue is the tell the duel reads).
      c.play(`skill.windup.${familyIdOf(s)}`, src, { len: s.windup, intensity: 0.55, pitch: registerOf(s) });
    },
  },
  stance: { ids: ['combat.guard'], play: (ev, c) => c.play('combat.guard', c.world.get(ev.id)) },
  whiff: { ids: ['combat.whiff'], play: (ev, c) => c.play('combat.whiff', c.world.get(ev.srcId)) },
  death: {
    ids: ['combat.death', 'combat.death.player', 'combat.death.boss'],
    play(ev, c) {
      const e = c.world.get(ev.id);
      if (!e) return;
      c.play(
        e === c.world.player ? 'combat.death.player' : isBoss(e) ? 'combat.death.boss' : 'combat.death',
        e,
      );
    },
  },
  respawn: { ids: ['combat.respawn'], play: (ev, c) => c.play('combat.respawn', c.world.get(ev.id)) },
  telegraph: {
    ids: ['combat.telegraph'],
    play: (ev, c) => c.play('combat.telegraph', ev.tele, { len: ev.tele.life }),
  },
  teleBurst: { ids: ['combat.boom'], play: (ev, c) => c.play('combat.boom', ev) },
  spin: {
    ids: ['combat.spin', 'skill.beat.*'],
    play(ev, c) {
      const beat = `skill.beat.${ev.skillId}`;
      c.play(hasSfx(beat) ? beat : 'combat.spin', c.world.get(ev.id));
    },
  },
  sheathe: {
    ids: ['combat.sheathe', 'combat.draw'],
    play: (ev, c) => c.play(ev.on ? 'combat.sheathe' : 'combat.draw', c.world.get(ev.id)),
  },
  status: {
    ids: ['skill.ice.frost'],
    play(ev, c) {
      if (ev.stacks <= 0) return;
      if (ev.status === 'snow-daze' || ev.status === 'chill' || ev.status === 'frost-trap')
        c.play('skill.ice.frost', c.world.get(ev.id), { intensity: 0.8 });
    },
  },
  pk: {
    ids: ['combat.pk', 'ui.toggle.off'],
    play: (ev, c) => c.play(ev.on ? 'combat.pk' : 'ui.toggle.off', c.world.get(ev.id)),
  },
  bossSpawn: { ids: ['world.boss.spawn'], play: (_ev, c) => c.play('world.boss.spawn') },
  bossDefeated: { ids: ['world.boss.defeated'], play: (_ev, c) => c.play('world.boss.defeated') },
  ladder: {
    ids: ['ui.notice', 'world.gong', 'combat.draw', 'ui.quest.complete', 'ui.lose', 'ui.toggle.off'],
    play(ev, c) {
      if (ev.phase === 'queue') c.play('ui.notice');
      else if (ev.phase === 'countdown') c.play('world.gong');
      else if (ev.phase === 'fight') c.play('combat.draw');
      else if (ev.phase === 'result') c.play(ev.won ? 'ui.quest.complete' : 'ui.lose');
      else c.play('ui.toggle.off');
    },
  },

  lock: {
    ids: ['combat.lock.on', 'combat.lock.drop'],
    play(ev, c) {
      if (ev.tgtId !== null) c.play('combat.lock.on', c.world.get(ev.srcId));
      else if (ev.dropped) c.play('combat.lock.drop', c.world.get(ev.srcId));
    },
  },
  duelChallenge: {
    ids: ['duel.challenge', 'ui.toggle.off'],
    play: (ev, c) => c.play(ev.accepted ? 'duel.challenge' : 'ui.toggle.off', c.world.get(ev.id)),
  },
  worldBoss: {
    ids: ['world.gong', 'world.boss.spawn', 'combat.ultimate.charge', 'world.boss.defeated', 'ui.lose'],
    play(ev, c) {
      if (ev.state === 'soon') c.play('world.gong', null, { dist: 9 });
      else if (ev.state === 'start') c.play('world.boss.spawn');
      else if (ev.state === 'enrage') c.play('combat.ultimate.charge', null, { len: 1 });
      else if (ev.state === 'end') c.play(ev.win ? 'world.boss.defeated' : 'ui.lose');
    },
  },
  worldEvent: {
    ids: ['world.gong', 'duel.go', 'ui.quest.progress', 'ui.quest.complete', 'ui.lose'],
    play(ev, c) {
      if (ev.state === 'soon') c.play('world.gong', null, { dist: 9 });
      else if (ev.state === 'start') c.play('duel.go');
      else if (ev.state === 'wave') c.play('ui.quest.progress');
      else c.play(ev.win ? 'ui.quest.complete' : 'ui.lose');
    },
  },
  dungeonStart: { ids: ['world.gong'], play: (_ev, c) => c.play('world.gong') },
  dungeonRoom: {
    ids: ['combat.draw', 'ui.quest.progress'],
    play: (ev, c) => c.play(ev.state === 'engaged' ? 'combat.draw' : 'ui.quest.progress'),
  },
  dungeonWipe: { ids: ['ui.lose'], play: (_ev, c) => c.play('ui.lose') },
  dungeonClear: { ids: ['ui.quest.complete'], play: (_ev, c) => c.play('ui.quest.complete') },
  trial: {
    ids: ['world.trial.start', 'world.trial.ring', 'ui.quest.complete', 'ui.lose', 'ui.toggle.off'],
    play(ev, c) {
      if (ev.state === 'start') c.play('world.trial.start');
      // Each ring a step up the scale.
      else if (ev.state === 'ring') c.play('world.trial.ring', null, { pitch: 2 ** (Math.min(12, ev.ring) / 12) });
      else if (ev.state === 'done') c.play('ui.quest.complete');
      else if (ev.state === 'fail') c.play('ui.lose');
      else c.play('ui.toggle.off');
    },
  },

  // ───────── skills in motion ─────────
  skillDash: { ids: ['move.dash.skill'], play: (ev, c) => c.play('move.dash.skill', c.world.get(ev.id)) },
  blink: {
    ids: ['skill.beat.*', 'skill.ice.warp', 'move.blink'],
    play(ev, c) {
      const s = SKILLS[ev.skillId];
      const beat = `skill.beat.${ev.skillId}`;
      if (s && hasSfx(beat)) {
        // Each warp of the chain its own beat, rising to the finisher.
        const n = (chainBeats.get(ev.id) ?? 0) + 1;
        chainBeats.set(ev.id, n);
        c.play(beat, c.world.get(ev.id), { intensity: beatIntensity(n, s.hits ?? 1) });
        return;
      }
      const fx = s?.fx;
      c.play(fx && ICE.has(fx) ? 'skill.ice.warp' : 'move.blink', c.world.get(ev.id));
    },
  },

  // ───────── movement ─────────
  // (in a bout the step has its own sounds: sfx/duelWatch.ts)
  dash: {
    ids: ['move.dash'],
    play: (ev, c) => {
      if (!duelMoveOf(c.world)?.inBout(ev.id)) c.play('move.dash', c.world.get(ev.id));
    },
  },
  jump: {
    ids: ['move.jump', 'move.jump.double', 'move.jump.cloud'],
    play: (ev, c) =>
      c.play(ev.cloud ? 'move.jump.cloud' : ev.air ? 'move.jump.double' : 'move.jump', c.world.get(ev.id)),
  },
  land: {
    ids: ['move.land'],
    play: (ev, c) => {
      if (!diveLanding(ev.id)) c.play('move.land', c.world.get(ev.id));
    },
  },
  airReset: { ids: ['move.airReset'], play: (ev, c) => c.play('move.airReset', c.world.get(ev.id)) },
  sink: { ids: ['move.splash'], play: (ev, c) => c.play('move.splash', c.world.get(ev.id)) },
  glide: {
    ids: ['move.glide.open'],
    play: (ev, c) => {
      if (ev.on) c.play('move.glide.open', c.world.get(ev.id));
    },
  },
  mount: {
    ids: ['move.mount.eagle', 'move.mount.sword', 'move.dismount'],
    play(ev, c) {
      const id = ev.mount === 'eagle' ? 'move.mount.eagle' : ev.mount ? 'move.mount.sword' : 'move.dismount';
      c.play(id, c.world.get(ev.id));
    },
  },
  travel: { ids: ['world.warp'], play: (_ev, c) => c.play('world.warp') },

  // ───────── growth, quests, the interface ─────────
  trained: {
    ids: ['ui.realmUp', 'ui.levelUp', 'ui.trained'],
    play(_ev, c) {
      const now = rankFor(c.world.progress.cultivationSpent).index;
      const before = rankSeen.index;
      rankSeen.index = now;
      if (before >= 0 && now > before)
        c.play(Math.floor(now / STAGES) > Math.floor(before / STAGES) ? 'ui.realmUp' : 'ui.levelUp');
      else c.play('ui.trained');
    },
  },
  qinggong: {
    ids: ['ui.learnArt', 'ui.trained'],
    play: (ev, c) => c.play(ev.level <= 1 ? 'ui.learnArt' : 'ui.trained'),
  },
  joinedSchool: { ids: ['ui.school'], play: (_ev, c) => c.play('ui.school') },
  learnedArt: { ids: ['ui.learnArt'], play: (_ev, c) => c.play('ui.learnArt') },
  moveLearned: { ids: ['ui.learnArt'], play: (_ev, c) => c.play('ui.learnArt') },
  title: { ids: ['ui.title'], play: (_ev, c) => c.play('ui.title') },
  quest: {
    ids: ['ui.quest.accept', 'ui.quest.progress', 'ui.quest.ready', 'ui.quest.complete'],
    play(ev, c) {
      const id = {
        accepted: 'ui.quest.accept',
        progress: 'ui.quest.progress',
        ready: 'ui.quest.ready',
        completed: 'ui.quest.complete',
      }[ev.status];
      c.play(id);
    },
  },
  dialog: { ids: ['ui.paper.open'], play: (_ev, c) => c.play('ui.paper.open') },
  notice: {
    ids: ['ui.notice', 'ui.error'],
    play: (ev, c) => c.play(isRefusal(ev.text) ? 'ui.error' : 'ui.notice'),
  },
  auto: {
    ids: ['ui.toggle.on', 'ui.toggle.off'],
    play: (ev, c) => c.play(ev.on ? 'ui.toggle.on' : 'ui.toggle.off'),
  },
  log: {
    ids: ['ui.trainTick'],
    play(ev, c) {
      // A cultivation payout (systems/cultivation.ts logs it on every tick that gained something).
      if (ev.channel === 'sys' && ev.text.startsWith('ฝึกปรือได้')) c.play('ui.trainTick');
    },
  },

  // ───────── things and money ─────────
  reward: { ids: ['econ.coin'], play: (ev, c) => c.play('econ.coin', c.world.get(ev.id)) },
  loot: { ids: ['econ.pickup'], play: (_ev, c) => c.play('econ.pickup') },
  equipped: {
    ids: ['econ.equip', 'ui.click'],
    play: (ev, c) => c.play(ev.item ? 'econ.equip' : 'ui.click'),
  },
  forge: {
    ids: ['econ.forge.ok', 'econ.forge.fail'],
    play: (ev, c) => c.play(ev.ok ? 'econ.forge.ok' : 'econ.forge.fail'),
  },
  work: {
    ids: ['econ.work.start', 'econ.work.done'],
    play(ev, c) {
      if (ev.phase === 'start') c.play('econ.work.start', c.world.get(ev.id));
      else if (ev.phase === 'done') c.play('econ.work.done', c.world.get(ev.id));
    },
  },
  gathered: { ids: ['econ.gather'], play: (_ev, c) => c.play('econ.gather') },
  crafted: { ids: ['econ.craft'], play: (_ev, c) => c.play('econ.craft') },
  tradeLevel: { ids: ['ui.levelUp'], play: (_ev, c) => c.play('ui.levelUp') },
  usedGood: { ids: ['econ.use'], play: (ev, c) => c.play('econ.use', c.world.get(ev.id)) },

  lootDrop: {
    ids: ['econ.drop.*'],
    play: (ev, c) => c.play(`econ.drop.${ev.rarity}`, { x: ev.x, y: ev.y }),
  },
  secret: {
    ids: ['world.chest.open', 'world.vista', 'ui.learnArt', 'world.lantern'],
    play(ev, c) {
      const id = { chest: 'world.chest.open', vista: 'world.vista', master: 'ui.learnArt', lantern: 'world.lantern' }[ev.kind];
      c.play(id);
    },
  },

  // ───────── people ─────────
  emote: {
    ids: ['social.emote', 'life.*'],
    play: (ev, c) => {
      // A townsperson's work beat (shared data/npcLife.ts `sound`): its own sound, nothing else.
      const life = lifeActOfEmote(ev.emote);
      if (life) {
        const s = LIFE_ACTS[life].sound;
        if (s) c.play(`life.${s}`, c.world.get(ev.id));
        return;
      }
      if (ev.emote && !ev.emote.startsWith('life-')) c.play('social.emote', c.world.get(ev.id));
    },
  },
  emoji: { ids: ['social.emoji'], play: (ev, c) => c.play('social.emoji', c.world.get(ev.id)) },
  party: { ids: ['social.party'], play: (_ev, c) => c.play('social.party') },
  bark: { ids: ['social.bark'], play: (ev, c) => c.play('social.bark', c.world.get(ev.id)) },
  danceStart: {
    ids: ['social.dance.start'],
    play: (ev, c) => c.play('social.dance.start', c.world.get(ev.leader)),
  },
  danceJoin: {
    ids: ['social.join'],
    play: (ev, c) => {
      if (ev.joined) c.play('social.join', c.world.get(ev.id));
    },
  },
  danceTurn: {
    ids: ['social.dance.beat'],
    play(ev, c) {
      if (ev.skipped) return;
      // The drum falls on every arrow's beat.
      const at = c.world.get(ev.dancer);
      for (const b of ev.beats) {
        const delay = b - c.world.time;
        if (delay >= -0.05 && delay < 12) c.play('social.dance.beat', at, { delay: Math.max(0, delay) });
      }
    },
  },
  danceArrow: {
    ids: ['social.dance.perfect', 'social.dance.hit', 'social.dance.miss'],
    play(ev, c) {
      const id =
        ev.grade === 'perfect'
          ? 'social.dance.perfect'
          : ev.grade === 'good'
            ? 'social.dance.hit'
            : 'social.dance.miss';
      c.play(id, c.world.get(ev.dancer));
    },
  },
  danceRound: { ids: ['social.dance.round'], play: (_ev, c) => c.play('social.dance.round') },
  danceEnd: { ids: ['social.dance.end'], play: (_ev, c) => c.play('social.dance.end') },

  // ───────── places ─────────
  meditate: {
    ids: ['world.meditate.start'],
    play: (ev, c) => {
      if (ev.on) c.play('world.meditate.start');
    },
  },
  trainingSpot: {
    ids: ['world.guqin'],
    play: (ev, c) => {
      if (ev.id) c.play('world.guqin');
    },
  },
  safeZone: {
    ids: ['world.safeZone.enter', 'world.safeZone.leave'],
    play: (ev, c) => c.play(ev.on ? 'world.safeZone.enter' : 'world.safeZone.leave'),
  },
  doorFade: { ids: ['world.door'], play: (_ev, c) => c.play('world.door') },
  changeFloor: { ids: ['world.stairs'], play: (_ev, c) => c.play('world.stairs') },
  jail: {
    ids: ['world.jail', 'world.door'],
    play: (ev, c) => c.play(ev.seconds > 0 ? 'world.jail' : 'world.door'),
  },
  carriage: {
    ids: ['ui.paper.open', 'world.door', 'world.carriage', 'world.carriage.arrive', 'ui.error'],
    play(ev, c) {
      if (ev.phase === 'menu') c.play('ui.paper.open');
      else if (ev.phase === 'board') c.play('world.door');
      else if (ev.phase === 'ride') c.play('world.carriage');
      else if (ev.phase === 'arrive') c.play('world.carriage.arrive');
      else if (ev.phase === 'cancel') c.play('ui.error');
    },
  },
};

/** Plays an event's sounds. Returns whether a rule spoke for it. */
export function playEventSfx(ev: WorldEvent, c: EventSfxCtx): boolean {
  const rule = EVENT_SFX[ev.type] as EventRule<WorldEvent['type']> | undefined;
  if (!rule) return false;
  try {
    rule.play(ev as never, c);
  } catch (e) {
    console.error(`sfx for '${ev.type}' failed`, e);
  }
  return true;
}
